import { useEffect, useState } from 'react'
import { fetchVariants, type VariantsResponse, type VariantStats } from '../../lib/api'
import { formatDuration } from '../../lib/format'
import { stepLabel } from '../stepLabels'
import { useFlowFilter } from '../FlowFilterContext'
import type { Audience, Variant } from '../../types'

interface Props { range: { from: number; to: number } }

const fmt = (n: number) => n.toLocaleString('da-DK')
const fmtPct = (p: number) => `${(p * 100).toFixed(1)}%`
const fmtPp = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(1)} pp`

const AUDIENCE_LABEL: Record<Audience, string> = {
  unge: 'Unge',
  aeldre: 'Ældre',
}

/**
 * Per-audience bucket inside the new /api/admin/variants response.
 * The shape is mirrored from the backend; we don't import it from lib/api.ts
 * (which the parallel agent owns) — we extend VariantsResponse locally so we
 * stay decoupled if that file lags.
 */
interface AudienceBucket {
  audience: Audience
  variants: VariantStats[]
  pValue: number | null
  significant: boolean
  sampleSizeNeeded: number | null
  topDropoff: Record<Variant, { step: string; rate: number } | null>
}

interface VariantsResponseV2 extends VariantsResponse {
  audiences?: AudienceBucket[]
}

/**
 * Normal-approximation Bayesian P(A > B) for two binomial proportions.
 * Approximates the Beta(1+leads, 1+sessions-leads) posteriors as Normal,
 * then computes P(p_A > p_B) = Φ((p_A - p_B) / sqrt(var_A + var_B)).
 *
 * For sample sizes >= 30, this matches Monte Carlo to within 1pp.
 */
function probAWins(aLeads: number, aSessions: number, bLeads: number, bSessions: number): number | null {
  if (aSessions < 5 || bSessions < 5) return null
  const pa = (aLeads + 1) / (aSessions + 2)
  const pb = (bLeads + 1) / (bSessions + 2)
  const va = (pa * (1 - pa)) / (aSessions + 3)
  const vb = (pb * (1 - pb)) / (bSessions + 3)
  const z = (pa - pb) / Math.sqrt(va + vb)
  return normalCdf(z)
}

function normalCdf(z: number): number {
  // Abramowitz & Stegun 7.1.26 — accurate to ~1.5e-7
  const t = 1 / (1 + 0.2316419 * Math.abs(z))
  const d = 0.3989422804014327 * Math.exp(-(z * z) / 2)
  const p = d * t * (0.31938153 + t * (-0.356563782 + t * (1.781477937 + t * (-1.821255978 + t * 1.330274429))))
  return z >= 0 ? 1 - p : p
}

export function ABTestPanel({ range }: Props) {
  const { audienceParam } = useFlowFilter()
  const [data, setData] = useState<VariantsResponseV2 | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setData(null); setError(null)
    // Note: lib/api.ts is being updated by a parallel agent to accept audience.
    // Cast through unknown so we can pass it today and TS catches up after merge.
    ;(fetchVariants as unknown as (
      range: { from: number; to: number },
      audience?: Audience
    ) => Promise<VariantsResponseV2>)(range, audienceParam)
      .then((d) => { if (!cancelled) setData(d) })
      .catch((e) => { if (!cancelled) setError(String(e)) })
    return () => { cancelled = true }
  }, [range.from, range.to, audienceParam])

  if (error) return <div className="card card-empty">Kunne ikke hente A/B data</div>
  if (!data) return <div className="card card-skeleton" aria-busy />

  // Prefer new audiences[] field; fall back to legacy single-audience response.
  const buckets: AudienceBucket[] = (() => {
    if (data.audiences && data.audiences.length > 0) {
      if (audienceParam) return data.audiences.filter((a) => a.audience === audienceParam)
      return data.audiences
    }
    // Legacy fallback — synthesize a single bucket from the flat fields.
    return [{
      audience: (audienceParam ?? 'unge') as Audience,
      variants: data.variants,
      pValue: data.pValue,
      significant: data.significant,
      sampleSizeNeeded: data.sampleSizeNeeded,
      topDropoff: data.topDropoff,
    }]
  })()

  if (buckets.length === 0) return <div className="card card-empty">Mangler variant-data</div>

  const showAudienceHeading = buckets.length > 1 || !!audienceParam

  return (
    <div className="ab-multi">
      {buckets.map((b) => (
        <AudiencePanel
          key={b.audience}
          bucket={b}
          showHeading={showAudienceHeading}
        />
      ))}
    </div>
  )
}

function AudiencePanel({ bucket, showHeading }: { bucket: AudienceBucket; showHeading: boolean }) {
  const chat = bucket.variants.find((v) => v.variant === 'chat')
  const calc = bucket.variants.find((v) => v.variant === 'calculator')

  if (!chat || !calc) {
    return (
      <div className="ab-panel">
        {showHeading && (
          <div className="ab-audience-heading">
            <span className="pill">{AUDIENCE_LABEL[bucket.audience]}</span>
          </div>
        )}
        <div className="card card-empty">Mangler variant-data for {AUDIENCE_LABEL[bucket.audience]}</div>
      </div>
    )
  }

  const pChatWins = probAWins(chat.completions, chat.sessions, calc.completions, calc.sessions)
  const pCalcWins = pChatWins !== null ? 1 - pChatWins : null
  const winner = pChatWins === null ? null : pChatWins > 0.95 ? 'chat' : pChatWins < 0.05 ? 'calculator' : null

  const totalSessions = chat.sessions + calc.sessions
  const minSessions = Math.min(chat.sessions, calc.sessions)
  const targetPerVariant = bucket.sampleSizeNeeded ?? 0
  const progress = targetPerVariant > 0 ? Math.min(1, minSessions / targetPerVariant) : 0
  const sessionsRemaining = Math.max(0, targetPerVariant - minSessions)

  // Visual scale: shared max for the rate-bar comparison
  const visualMax = Math.max(chat.ciHigh, calc.ciHigh, 0.05)

  let recommendation: string
  let recTone: 'good' | 'mid' | 'wait'
  if (winner === 'chat') {
    recommendation = `Anbefaling: Skift fuldt til chat. P(chat vinder) = ${fmtPct(pChatWins!)}. Forventet løft: ${fmtPp((chat.rate - calc.rate) * 100)}.`
    recTone = 'good'
  } else if (winner === 'calculator') {
    recommendation = `Anbefaling: Skift fuldt til beregner. P(beregner vinder) = ${fmtPct(pCalcWins!)}. Forventet løft: ${fmtPp((calc.rate - chat.rate) * 100)}.`
    recTone = 'good'
  } else if (bucket.significant) {
    recommendation = `Resultat er signifikant (p=${bucket.pValue?.toFixed(3) ?? '—'}) — overvej at promovere bedste variant.`
    recTone = 'mid'
  } else if (totalSessions < 100) {
    recommendation = `For lidt data — vent på mindst 50 sessioner pr. variant før beslutning.`
    recTone = 'wait'
  } else if (sessionsRemaining > 0) {
    recommendation = `Resultat er endnu ikke konkluderende. Du mangler ca. ${fmt(sessionsRemaining)} sessioner pr. variant for at detektere et 10pp-løft med 80% styrke.`
    recTone = 'wait'
  } else {
    recommendation = `Stikprøve nået, men forskellen er for lille til at være statistisk meningsfuld. Effekten er reelt < 10pp.`
    recTone = 'mid'
  }

  return (
    <div className="ab-panel">
      {showHeading && (
        <div className="ab-audience-heading">
          <span className="pill">{AUDIENCE_LABEL[bucket.audience]}</span>
          <span className="muted small">A/B for målgruppe</span>
        </div>
      )}

      <div className="card ab-headline">
        <div className="card-header">
          <div className="card-title">A/B test status</div>
          <div className="card-subtitle">Sammenligning af chat vs beregner i valgte periode</div>
        </div>
        <div className="ab-bayes">
          <div className="ab-bayes-row">
            <div className="ab-bayes-label">P(chat vinder)</div>
            <div className="ab-bayes-bar">
              <div
                className="ab-bayes-bar-fill ab-tone-chat"
                style={{ width: pChatWins !== null ? `${pChatWins * 100}%` : '0%' }}
              />
            </div>
            <div className="ab-bayes-num">{pChatWins !== null ? fmtPct(pChatWins) : '—'}</div>
          </div>
          <div className="ab-bayes-row">
            <div className="ab-bayes-label">P(beregner vinder)</div>
            <div className="ab-bayes-bar">
              <div
                className="ab-bayes-bar-fill ab-tone-calc"
                style={{ width: pCalcWins !== null ? `${pCalcWins * 100}%` : '0%' }}
              />
            </div>
            <div className="ab-bayes-num">{pCalcWins !== null ? fmtPct(pCalcWins) : '—'}</div>
          </div>
        </div>
        <div className={`ab-recommendation ab-rec-${recTone}`}>{recommendation}</div>
      </div>

      <div className="ab-grid">
        {[chat, calc].map((v) => {
          const isWinner = winner === v.variant
          const dropoff = bucket.topDropoff[v.variant]
          return (
            <div key={v.variant} className={`card ab-variant-card ${isWinner ? 'ab-winner' : ''}`}>
              <div className="ab-variant-head">
                <div className="ab-variant-name">{v.variant === 'chat' ? 'Chat' : 'Beregner'}</div>
                {isWinner && <div className="ab-winner-badge">Bedste variant</div>}
              </div>
              <div className="ab-variant-rate">
                <div className="ab-variant-rate-num">{fmtPct(v.rate)}</div>
                <div className="muted small">conversion rate</div>
              </div>
              <div className="ab-ci-track" aria-label="confidence interval">
                <div
                  className="ab-ci-band"
                  style={{
                    left: `${(v.ciLow / visualMax) * 100}%`,
                    width: `${((v.ciHigh - v.ciLow) / visualMax) * 100}%`,
                  }}
                />
                <div className="ab-ci-marker" style={{ left: `${(v.rate / visualMax) * 100}%` }} />
              </div>
              <div className="muted small ab-ci-text">
                95% CI: {fmtPct(v.ciLow)} – {fmtPct(v.ciHigh)}
              </div>
              <dl className="ab-variant-stats">
                <div><dt>Sessioner</dt><dd>{fmt(v.sessions)}</dd></div>
                <div><dt>Leads</dt><dd>{fmt(v.completions)}</dd></div>
                <div><dt>Median tid til lead</dt><dd>{v.medianTimeMs ? formatDuration(v.medianTimeMs) : '—'}</dd></div>
                <div><dt>Største drop-off</dt><dd>{dropoff ? `${stepLabel(v.variant, dropoff.step, bucket.audience)} (${fmtPct(dropoff.rate)})` : '—'}</dd></div>
              </dl>
            </div>
          )
        })}
      </div>

      <div className="card ab-samplesize">
        <div className="card-header">
          <div className="card-title">Sample-size status</div>
          <div className="card-subtitle">For at detektere 10pp løft med 80% styrke</div>
        </div>
        <div className="ab-progress-wrap">
          <div className="ab-progress-bar">
            <div className="ab-progress-fill" style={{ width: `${progress * 100}%` }} />
          </div>
          <div className="ab-progress-meta">
            <span><strong>{fmt(minSessions)}</strong> / {fmt(targetPerVariant)} sessioner pr. variant</span>
            {sessionsRemaining > 0
              ? <span className="muted">{fmt(sessionsRemaining)} tilbage</span>
              : <span className="tone-up">Mål nået</span>}
          </div>
        </div>
        <div className="ab-stats-row">
          <span>Frequentist p-værdi: <strong>{bucket.pValue?.toFixed(3) ?? '—'}</strong></span>
          <span className="dot">·</span>
          <span>{bucket.significant ? <span className="tone-up">Signifikant ved α=0.05</span> : <span className="muted">Ikke signifikant</span>}</span>
        </div>
      </div>
    </div>
  )
}
